const { push: Push } = require('connected-react-router');

const Storage = require('utils/storage');
const CreateCommunicationService = require('services/communication');
const { DEFAULT_ROLE_GROUP_FILTERS } = require('../../utils/constants');
const {
    startPollingNotifs,
    stopPollingNotifs
} = require('../../initializers/notifications');
const AuthConstants = require('./constants');
const Debug = require('./debug');
const Sso = require('./sso');
const RoutingTable = require('./routing-table');
const BootstrapCache = require('./bootstrap-cache');

module.exports = {
    switchSchoolSession,
    prepareSchoolSwitch,
    bootstrapSwitchedSchoolSession,
    syncLoginContextForSwitchedSchool,
    resolveProfileSessionKind
};

async function switchSchoolSession({
    schoolId,
    leaveSchoolIdAfterSwitch,
    afterSwitchFunc,
    redirect,
    target
}) {

    const {
        api,
        actions,
        redux,
        selectors
    } = resolveContextDependencies();

    const token = selectors.getApiToken(redux.getState());
    const isCurrent = () => selectors.getIsAuthenticated(redux.getState())
        && selectors.getApiToken(redux.getState()) === token;

    stopPollingNotifs();

    await prepareSchoolSwitch({
        schoolId,
        redirect,
        target,
        isCurrent
    });

    if (!isCurrent()) {
        return;
    }

    const session = await api.profileManagement.switchSchool({
        schoolId
    });

    if (!isCurrent()) {
        return;
    }

    const completed = await bootstrapSwitchedSchoolSession({
        session,
        requestedSchoolId: schoolId
    });

    if (!completed) {
        return;
    }

    startPollingNotifs(redux.dispatch);

    if (afterSwitchFunc) {
        afterSwitchFunc();
    }

    if (leaveSchoolIdAfterSwitch) {
        await redux.dispatch(actions.profileManagement.leaveSchool({
            schoolId: leaveSchoolIdAfterSwitch
        }));
    }
}

async function prepareSchoolSwitch({
    schoolId,
    redirect,
    target,
    isCurrent = () => true
}) {

    const {
        actions,
        handlers,
        redux,
        selectors,
        communication
    } = resolveContextDependencies();

    const schoolName = selectors.getSchoolById(redux.getState(), schoolId)?.name;

    Debug.recordFlow('school_switch_prepare', {
        schoolId,
        schoolName
    });

    redux.dispatch(actions.app.setSchoolSwitchingState({
        switchingToSchool: schoolName
    }));

    redux.dispatch(actions.app.setMainAppLoading({
        appLoading: true
    }));

    await handlers.communication.stopGlobalListening();

    if (!isCurrent()) {
        return;
    }

    await communication.destroySession();

    if (!isCurrent()) {
        return;
    }

    await communication.shutdown();

    if (!isCurrent()) {
        return;
    }

    redux.dispatch(Push(RoutingTable.getAppLoaderRoute({ redirect, target })));

    redux.dispatch(actions.app.setRoleGroupFilter(DEFAULT_ROLE_GROUP_FILTERS));
    redux.dispatch(actions.app.setShouldRefreshSearch({
        discover: true,
        group: true
    }));
    redux.dispatch(actions.app.clearValuesForSchoolSwitch());
}

async function bootstrapSwitchedSchoolSession({
    session,
    requestedSchoolId
}) {

    const {
        actions,
        handlers,
        redux,
        selectors,
        communication
    } = resolveContextDependencies();

    const normalizedSession = normalizeSwitchedSchoolSession({
        requestedSchoolId,
        session
    });

    const {
        nearpeer,
        isVerified,
        schoolId
    } = normalizedSession;
    let { twilio: twilioToken } = normalizedSession;

    Debug.recordFlow('school_switch_bootstrap', {
        schoolId,
        hasNearpeerToken: !!nearpeer,
        hasTwilioToken: !!twilioToken,
        profileSessionKind: resolveProfileSessionKind({ session: normalizedSession })
    });

    if (!nearpeer) {
        const err = new Error('School switch session is missing an API token.');
        err.code = 'SCHOOL_SWITCH_MISSING_TOKEN';
        err.details = {
            schoolId,
            sessionKeys: Object.keys(session || {})
        };
        throw err;
    }

    BootstrapCache.clear();
    Storage.set(AuthConstants.token.storageKey, nearpeer);

    redux.dispatch(actions.auth.login.success({
        result: {
            tokens: {
                nearpeer,
                twilio: twilioToken
            },
            isVerified,
            schoolId
        }
    }));

    const isCurrent = () => selectors.getIsAuthenticated(redux.getState())
        && selectors.getApiToken(redux.getState()) === nearpeer;

    if (!twilioToken) {
        const twilioResponse = await redux.dispatch(actions.auth.updateTwilioToken());
        twilioToken = twilioResponse?.token
            || twilioResponse?.result?.token
            || selectors.getTwilioToken(redux.getState());
    }

    if (!isCurrent()) {
        return false;
    }

    await communication.initialize({
        reset: true
    });

    if (!isCurrent()) {
        return false;
    }

    const userResponse = await redux.dispatch(actions.dataFetching.fetchCurrentUser());

    if (!isCurrent()) {
        return false;
    }

    await handlers.communication.startGlobalListening();

    if (!isCurrent()) {
        return false;
    }

    redux.dispatch(actions.dataFetching.fetchAnnouncementConversations());

    const bootstrapResponse = await redux.dispatch(actions.dataFetching.fetchAppEntities({
        entities: [
            'schools',
            'schoolDetails',
            'badgeTypes',
            'conversationStarters',
            'yearsHired',
            'offices',
            'roles',
            'interests',
            'classes',
            'preferences',
            'roleGroups'
        ],
        schoolId
    }));

    if (!isCurrent()) {
        return false;
    }

    syncLoginContextForSwitchedSchool({
        schoolId
    });

    await redux.dispatch(actions.dataFetching.fetchNotifications());

    if (!isCurrent()) {
        return false;
    }

    await BootstrapCache.save({
        token: nearpeer,
        schoolId,
        user: userResponse?.payload?.result,
        bootstrap: bootstrapResponse?.payload?.result,
        isCurrent
    });

    if (!isCurrent()) {
        return false;
    }

    redux.dispatch(actions.app.setMainAppLoading({
        appLoading: false
    }));
    redux.dispatch(actions.app.setSchoolSwitchingState({
        switchingToSchool: false
    }));

    return true;
}

function syncLoginContextForSwitchedSchool({
    schoolId
}) {

    const {
        actions,
        redux,
        selectors
    } = resolveContextDependencies();

    const school = selectors.getSchoolById(redux.getState(), schoolId);

    Debug.recordFlow('school_switch_login_context_sync', {
        schoolId,
        hasSSO: !!school?.sso
    });

    redux.dispatch(actions.auth.updateLoginContext({
        authMethod: null,
        schoolId,
        sso: Sso.normalizeSSO(school?.sso),
        verificationToken: null,
        roleId: null
    }));
}

function resolveProfileSessionKind({ session = {}, currentUser } = {}) {

    if (session.isSecondaryProfile || session.user?.isSecondaryProfile || currentUser?.isSecondaryProfile) {
        return 'secondary';
    }

    return 'primary';
}

function normalizeSwitchedSchoolSession({
    requestedSchoolId,
    session = {}
} = {}) {

    const user = session.user || {};
    const isVerified = typeof session.isVerified === 'boolean'
        ? session.isVerified
        : typeof user.verified === 'boolean'
            ? user.verified
            : true;

    return {
        ...session,
        nearpeer: session.nearpeer || session.token,
        twilio: session.twilio,
        isVerified,
        schoolId: session.schoolId || user.schoolId || requestedSchoolId,
        user
    };
}

function resolveContextDependencies() {

    const {
        context
    } = require('app-context');

    if (!context?.redux?.hooks) {
        throw new Error('AuthManagement profile-session operations require initialized app context.');
    }

    return {
        api: context.api.nearpeer,
        actions: context.actions,
        redux: context.redux.hooks,
        handlers: context.handlers,
        selectors: context.selectors.all,
        communication: CreateCommunicationService.fromContext(context)
    };
}
